import { createColumnHelper } from '@tanstack/react-table'
import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { getBusinessInvoicesService } from '../../../services' // Assuming this path is correct
import logger from '../../../utils/logger' // Assuming this path is correct
import GenericTable from '../../generic-data-table/GenericTable' // Assuming this path is correct
import { ReloadIcon } from '@radix-ui/react-icons' // Assuming this is correctly installed and imported

const columnHelper = createColumnHelper()

// Skeleton Row Component for the table body
const SkeletonRow = ({ columnsCount }) => (
  <tr className="animate-pulse">
    {Array.from({ length: columnsCount }).map((_, idx) => (
      <td key={idx} className="px-6 py-4 whitespace-nowrap text-sm">
        <div
          className={`h-4 bg-gray-300 rounded ${idx === 0 ? 'w-10' : idx < 3 ? 'w-24' : 'w-32'}`}
        ></div>
      </td>
    ))}
  </tr>
)

function BusinessInvoicesComponent() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [invoices, setInvoices] = useState([])
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10, // Default limit
    totalPages: 1,
  })

  const fetchInvoice = useCallback(async (page = 1, limit = 10) => {
    try {
      setLoading(true)
      setError('')
      // Simulate API delay for testing skeleton
      // await new Promise(resolve => setTimeout(resolve, 1500));
      const { data } = await getBusinessInvoicesService(page, limit)
      if (data && data.status === 200 && data.data) {
        setInvoices(data.data.invoices || [])
        setPagination({
          page: data.data.pagination.page,
          limit: data.data.pagination.limit,
          totalPages: data.data.pagination.totalPages,
        })
      } else {
        setError(
          data?.message || 'Failed to load invoices. The server returned an unexpected response.'
        )
      }
    } catch (e) {
      logger.error('Error fetching invoices:', e)
      setError(
        'Something went wrong while fetching invoices. Please check your connection and try again.'
      )
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchInvoice(pagination.page, pagination.limit)
  }, [fetchInvoice, pagination.page, pagination.limit])

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }))
  }

  const handleLimitChange = (newLimit) => {
    setPagination((prev) => ({
      ...prev,
      limit: parseInt(newLimit, 10),
      page: 1, // Reset to page 1 when limit changes
    }))
  }

  const handleRefresh = () => {
    // Fetch with current page and limit
    fetchInvoice(pagination.page, pagination.limit)
  }

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'srNo',
        header: 'Sr. No.',
        cell: ({ row }) => (pagination.page - 1) * pagination.limit + row.index + 1,
      }),
      columnHelper.accessor('invoiceId', {
        header: 'Invoice ID',
        cell: (info) => info.getValue() || 'N/A',
      }),
      columnHelper.accessor('amountPaid', {
        header: 'Amount Paid',
        cell: (info) => {
          const amount = info.getValue()

          const currency = info?.row?.original?.currency.toUpperCase()
          return typeof amount === 'number' ? `${currency} ${(amount / 100).toFixed(2)}` : 'N/A'
        },
      }),
      columnHelper.accessor('currency', {
        header: 'Currency',
        cell: (info) => info.getValue()?.toUpperCase() || 'N/A',
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: (info) => {
          const status = info.getValue()
          let colorClass = 'text-gray-700 bg-gray-100' // Default
          if (status === 'paid') colorClass = 'text-green-700 bg-green-100'
          else if (status === 'open') colorClass = 'text-yellow-700 bg-yellow-100'
          else if (status === 'draft') colorClass = 'text-blue-700 bg-blue-100'
          else if (status === 'void' || status === 'uncollectible')
            colorClass = 'text-red-700 bg-red-100'

          return status ? (
            <span
              className={`px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${colorClass} capitalize`}
            >
              {status.replace('_', ' ')}
            </span>
          ) : (
            <span className="text-gray-400 italic">N/A</span>
          )
        },
      }),
      columnHelper.accessor('invoicePdf', {
        header: 'Invoice PDF',
        cell: (info) =>
          info.getValue() ? (
            <a
              href={info.getValue()}
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 hover:text-indigo-800 underline transition-colors duration-150 ease-in-out"
            >
              Download PDF
            </a>
          ) : (
            <span className="text-gray-400 italic">N/A</span>
          ),
      }),
      columnHelper.accessor('hostedInvoiceUrl', {
        header: 'Hosted Invoice',
        cell: (info) =>
          info.getValue() ? (
            <a
              href={info.getValue()}
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 hover:text-indigo-800 underline transition-colors duration-150 ease-in-out"
            >
              View Receipt
            </a>
          ) : (
            <span className="text-gray-400 italic">N/A</span>
          ),
      }),
    ],
    [pagination.page, pagination.limit]
  )

  const renderContent = () => {
    // Skeleton for initial full page load
    if (loading && !invoices.length && pagination.page === 1) {
      return (
        <div className="overflow-hidden shadow border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.id || col.header?.toString()}
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    <div
                      className={`h-4 bg-gray-300 rounded ${col.id === 'srNo' ? 'w-12' : col.id === 'status' ? 'w-20' : 'w-28'}`}
                    ></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Array.from({ length: pagination.limit > 0 ? pagination.limit : 5 }).map((_, i) => (
                <SkeletonRow key={i} columnsCount={columns.length} />
              ))}
            </tbody>
          </table>
          {/* Skeleton Pagination */}
          <div className="flex justify-between items-center px-4 py-3 border-t border-gray-200 bg-white animate-pulse sm:px-6">
            <div className="h-8 w-36 bg-gray-300 rounded"></div> {/* Pagination info */}
            <div className="flex space-x-2">
              <div className="h-8 w-20 bg-gray-300 rounded"></div> {/* Prev button */}
              <div className="h-8 w-20 bg-gray-300 rounded"></div> {/* Next button */}
            </div>
          </div>
        </div>
      )
    }

    // No invoices found (after loading is complete)
    if (!loading && !invoices.length) {
      return (
        <div className="text-center py-12 px-6 border border-dashed border-gray-300 rounded-lg bg-white">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
            />
          </svg>
          <h3 className="mt-3 text-lg font-semibold text-gray-800">No Invoices Found</h3>
          <p className="mt-1 text-sm text-gray-500">
            There are currently no business invoices to display. Try adjusting your filters or check
            back later.
          </p>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="mt-4 flex items-center mx-auto gap-2 px-4 py-1.5 border border-gray-300 bg-white text-sm font-medium text-gray-700 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <ReloadIcon className={loading ? 'animate-spin w-4 h-4' : 'w-4 h-4'} />
            Refresh Invoices
          </button>
        </div>
      )
    }

    // Display table
    return (
      <GenericTable
        columns={columns}
        data={invoices}
        isLoading={loading} // GenericTable uses this for its own overlay when refreshing/paginating
        pagination={pagination}
        onPageChange={handlePageChange}
      // Assuming GenericTable might take these or similar props for items per page
      // onLimitChange={handleLimitChange}
      // limitOptions={[10, 25, 50, 100]}
      />
    )
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-6 bg-gray-50 min-h-[calc(100vh-theme_header_height)]">
      {' '}
      {/* Adjust min-h if you have a fixed header */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Business Invoices</h1>
        <div className="relative inline-block mr-3">
          <select
            id="itemsPerPage"
            name="itemsPerPage"
            value={pagination.limit}
            onChange={(e) => handleLimitChange(e.target.value)}
            disabled={loading}
            className="block w-full sm:w-auto pl-3 pr-12 py-2 text-sm border-gray-300
               focus:outline-none focus:ring-indigo-500 focus:border-indigo-500
               rounded-md shadow-sm transition duration-150 ease-in-out"
            style={{ WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none' }}
          >
            <option value="10">10 per page</option>
            <option value="25">25 per page</option>
            <option value="50">50 per page</option>
            <option value="100">100 per page</option>
          </select>

          {/* custom arrow icon */}
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-800">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </span>
        </div>
      </div>
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md shadow" role="alert">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm-.707-4.293a1 1 0 00-1.414-1.414L10 10.586l-2.293-2.293a1 1 0 00-1.414 1.414L8.586 12l-2.293 2.293a1 1 0 101.414 1.414L10 13.414l2.293 2.293a1 1 0 001.414-1.414L11.414 12l2.293-2.293a1 1 0 00-1.414-1.414L10 10.586z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-semibold text-red-700">Request Failed</p>
              <p className="text-sm text-red-600 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}
      {renderContent()}
    </div>
  )
}

export default BusinessInvoicesComponent
